// Shown when a saved workflow is opened: what it does, then one upload slot
// per saved source. Each file fills its existing source (never a new one) and
// is checked against the saved columns and types; problems are shown as
// warnings, and the user decides whether to go on. The saved code then runs
// on the files without asking the AI again.
import { Button } from "antd";
import { ArrowRight } from "lucide-react";
import { toast } from "react-toastify";
import { readWorkbook, useSourceChecks } from "../shared/sourceFiles";
import { useBuilder } from "./BuilderContext";
import { SourceSlot } from "./RunDialog";

const UseWorkflowScreen = ({ onContinue }) => {
  const { state, dispatch } = useBuilder();
  const { workflow, files } = state;
  const missing = workflow.sources.filter((source) => !files[source.id]);
  const checks = useSourceChecks(workflow.sources, files);

  const pickFile = async (sourceId, picked) => {
    try {
      dispatch({ type: "attachSample", id: sourceId, file: await readWorkbook(picked) });
    } catch (error) {
      toast.error(`Could not read ${picked.name}: ${error.message}`);
    }
  };

  return (
    <div className="flex h-full justify-center overflow-y-auto bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:20px_20px] p-6">
      <div className="w-full max-w-2xl space-y-4">
        <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800">{workflow.name}</h2>
          {workflow.description && <p className="text-[13px] leading-relaxed text-gray-600">{workflow.description}</p>}
          {workflow.steps.length > 0 && (
            <ol className="space-y-1 border-t border-gray-100 pt-2">
              {workflow.steps.map((step, index) => (
                <li key={step.id} className="text-xs text-gray-600">
                  <span className="font-medium text-gray-800">
                    {index + 1}. {step.label || "Step"}
                  </span>
                  {step.params?.summary && <span className="text-gray-500"> — {step.params.summary}</span>}
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="text-sm font-semibold text-gray-800">Upload your file{workflow.sources.length > 1 ? "s" : ""}</div>
          {workflow.sources.map((source) => (
            <SourceSlot
              key={source.id}
              source={source}
              file={files[source.id]}
              check={checks[source.id]}
              onFile={(picked) => pickFile(source.id, picked)}
              onSheet={(sheetName) => dispatch({ type: "attachSample", id: source.id, file: files[source.id], sheetName })}
              onOverride={(column, letter) => dispatch({ type: "setColumnOverride", id: source.id, column, letter })}
            />
          ))}
          <div className="flex items-center justify-between pt-1">
            <button type="button" onClick={onContinue} className="text-xs text-gray-500 hover:text-gray-800">
              Open without files
            </button>
            <Button type="primary" icon={<ArrowRight size={14} />} disabled={missing.length > 0} onClick={onContinue}>
              Run workflow
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UseWorkflowScreen;
